import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Filter, Loader2 } from 'lucide-react';

import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';
import CriteriaForm from '../components/deal-scanner/CriteriaForm';
import DealCard from '../components/deal-scanner/DealCard';
import TopMatches from '../components/deal-scanner/TopMatches';
import SavedSearchList from '../components/deal-scanner/SavedSearchList';
import SampleDataBanner from '../components/deal-scanner/SampleDataBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORT_MODES = ['best_match', 'highest_cash_flow', 'highest_cap_rate', 'lowest_price', 'newest'];

export default function DealScanner() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [sortBy, setSortBy] = useState('best_match');
  const [initialCriteria, setInitialCriteria] = useState(null);
  const [criteriaSnapshot, setCriteriaSnapshot] = useState(null);
  const [showCriteria, setShowCriteria] = useState(true);
  const [filters, setFilters] = useState({ minMatch: 0 });

  // Wave 1B.2A — feature-access gates use `effective_capabilities` so a
  // Free + Research Premium participant can reach the Deal Scanner Pro
  // surfaces. Billing-display elsewhere continues to read `subscription_plan`.
  const effCaps = user?.effective_capabilities || [];
  const isPaid = effCaps.includes('ai_advisor') || user?.subscription_plan === 'pro' || user?.subscription_plan === 'premium';
  const isPremium = effCaps.includes('portfolio_ai') || user?.subscription_plan === 'premium';

  useEffect(() => {
    (async () => {
      try {
        const [uRes, pRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/investor-profile`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setUser(uRes.data);
        setProfile(pRes.data);
      } catch {
        navigate('/');
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (user && ((user.effective_capabilities || []).includes('ai_advisor') || user.subscription_plan === 'pro' || user.subscription_plan === 'premium')) {
      loadSavedSearches();
    }
  }, [user]);

  const loadSavedSearches = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/deal-scanner/saved-searches`, {
        withCredentials: true,
      });
      setSavedSearches(res.data || []);
    } catch (e) {
      // Free users get empty array; ignore
    }
  };

  const runScan = async (criteria, sortOverride) => {
    setScanning(true);
    setResults(null);
    setCriteriaSnapshot(criteria);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deal-scanner/scan?sort_by=${sortOverride || sortBy}`,
        criteria,
        { withCredentials: true },
      );
      setResults(res.data);
      setShowCriteria(false);
      if (res.data.total_matches === 0) {
        toast.info(t('dealScanner.noResults'));
      } else {
        toast.success(t('dealScanner.scanComplete', { count: res.data.total_matches }));
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 403 && detail === 'free_scan_limit_reached') {
        toast.error(t('dealScanner.freeScanLimitReached'));
      } else if (status === 401) {
        navigate('/');
      } else {
        toast.error(detail || t('dealScanner.scanFailed'));
      }
    } finally {
      setScanning(false);
    }
  };

  const handleSaveSearch = async (name, criteria) => {
    if (!isPaid) {
      toast.error(t('dealScanner.saveSearchProRequired'));
      navigate('/pricing');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/deal-scanner/saved-searches`,
        { name, criteria },
        { withCredentials: true },
      );
      toast.success(t('dealScanner.saveSearchSaved'));
      loadSavedSearches();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === 'pro_saved_search_cap_reached') {
        toast.error(t('dealScanner.proSavedCapReached'));
      } else {
        toast.error(t('dealScanner.saveSearchFailed'));
      }
    }
  };

  const handleRunSaved = (s) => {
    setInitialCriteria(s.criteria);
    setShowCriteria(true);
    runScan(s.criteria);
  };

  const handleRenameSaved = async (s) => {
    const newName = window.prompt(t('dealScanner.renamePrompt'), s.name);
    if (!newName || newName.trim() === s.name) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/deal-scanner/saved-searches/${s.search_id}`,
        { name: newName.trim(), criteria: s.criteria },
        { withCredentials: true },
      );
      toast.success(t('dealScanner.renameSuccess'));
      loadSavedSearches();
    } catch {
      toast.error(t('dealScanner.renameFailed'));
    }
  };

  const handleDeleteSaved = async (s) => {
    if (!window.confirm(t('dealScanner.deleteConfirm', { name: s.name }))) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/deal-scanner/saved-searches/${s.search_id}`,
        { withCredentials: true },
      );
      toast.success(t('dealScanner.deleteSuccess'));
      loadSavedSearches();
    } catch {
      toast.error(t('dealScanner.deleteFailed'));
    }
  };

  const handleAnalyze = (result) => {
    // Hand off to the existing analysis engine via URL query prefill.
    const q = new URLSearchParams();
    q.set('address', result.address + (result.city ? `, ${result.city}` : ''));
    q.set('price', String(result.asking_price || ''));
    q.set('units', String(result.num_units || 1));
    if (result.annual_income) q.set('income', String(result.annual_income));
    if (result.annual_property_tax) q.set('tax', String(result.annual_property_tax));
    if (result.annual_insurance) q.set('insurance', String(result.annual_insurance));
    if (result.monthly_maintenance) q.set('maint', String(result.monthly_maintenance));
    if (criteriaSnapshot?.down_payment_percent) q.set('dp', String(criteriaSnapshot.down_payment_percent));
    q.set('source', 'deal_scanner');
    navigate(`/analyze?${q.toString()}`);
  };

  const handleShare = (result) => {
    const url = `${window.location.origin}/deal-scanner`;
    const text = `${result.address} — ${t('dealScanner.dealMatch')}: ${result.deal_match_score}% · ${result.property_type}`;
    if (navigator.share) {
      navigator.share({ title: 'RendPleX', text, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success(t('dealScanner.shareCopied'));
    }
  };

  const changeSort = (mode) => {
    setSortBy(mode);
    if (criteriaSnapshot) runScan(criteriaSnapshot, mode);
  };

  const filteredResults = useMemo(() => {
    if (!results?.results) return [];
    return results.results.filter((r) => (r.deal_match_score || 0) >= filters.minMatch);
  }, [results, filters]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] dark:bg-[#0B0E14]">
        <Loader2 className="w-8 h-8 animate-spin text-[#45C04A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex flex-col">
      {/* Header */}
      <header className="bg-white/70 dark:bg-[#0B0E14]/80 backdrop-blur border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <RendPlexLogo size="md" />
          </Link>
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <LanguageSwitcher />
            <Button
              data-testid="scanner-back-to-dashboard"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 dark:text-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('common.back')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 flex-1 space-y-8">
        {/* Hero */}
        <div data-testid="scanner-hero" className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#45C04A]/10 text-[#12355B] dark:text-[#45C04A] px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Search className="w-3.5 h-3.5" /> {t('dealScanner.navTitle')}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#12355B] dark:text-white">
            {t('dealScanner.heroTitle')}
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base max-w-2xl">
            {t('dealScanner.heroSubtitle')}
          </p>
        </div>

        <SampleDataBanner label={results?.source?.label} />

        {/* Saved searches (Pro/Premium) or upgrade prompt (Free) */}
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-[#12355B] dark:text-white flex items-center gap-2">
            {t('dealScanner.savedSearchesTitle')}
          </h2>
          <SavedSearchList
            items={savedSearches}
            locked={!isPaid}
            onRun={handleRunSaved}
            onDelete={handleDeleteSaved}
            onRename={handleRenameSaved}
            onUpgrade={() => navigate('/pricing')}
          />
        </section>

        {/* Criteria form (collapsible) */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#12355B] dark:text-white">
              {t('dealScanner.criteriaTitle')}
            </h2>
            {results && (
              <Button
                data-testid="scanner-toggle-criteria"
                onClick={() => setShowCriteria((v) => !v)}
                variant="outline"
                className="rounded-lg text-xs px-3 py-1.5"
              >
                {showCriteria ? t('dealScanner.hideCriteria') : t('dealScanner.editCriteria')}
              </Button>
            )}
          </div>
          {showCriteria && (
            <CriteriaForm
              initial={initialCriteria}
              hasProfile={!!profile?.is_complete}
              onScan={(c) => runScan(c)}
              onSaveSearch={handleSaveSearch}
              scanning={scanning}
              canSaveSearch={isPaid}
              saveSearchTooltip={t('dealScanner.saveSearchProRequired')}
            />
          )}
        </section>

        {/* Results */}
        {scanning && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#45C04A] mx-auto" />
            <p className="text-sm text-slate-500 mt-2">{t('dealScanner.scanning')}</p>
          </div>
        )}

        {results && !scanning && (
          <>
            {/* Header + sort */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 data-testid="scanner-results-count" className="text-xl font-bold text-[#12355B] dark:text-white">
                  {t('dealScanner.resultsCount', { count: results.total_matches })}
                </h2>
                {results.truncated && !isPaid && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    {t('dealScanner.freeTruncated', { count: results.returned })}{' '}
                    <button
                      className="underline"
                      data-testid="scanner-truncated-upgrade"
                      onClick={() => navigate('/pricing')}
                    >
                      {t('dealScanner.upgrade')}
                    </button>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <select
                  data-testid="scanner-sort-select"
                  value={sortBy}
                  onChange={(e) => changeSort(e.target.value)}
                  className="border border-slate-200 dark:border-slate-700 dark:bg-[#131824] dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
                >
                  {SORT_MODES.map((m) => (
                    <option key={m} value={m}>{t(`dealScanner.sort.${m}`)}</option>
                  ))}
                </select>
                <select
                  data-testid="scanner-filter-min-match"
                  value={filters.minMatch}
                  onChange={(e) => setFilters({ ...filters, minMatch: Number(e.target.value) })}
                  className="border border-slate-200 dark:border-slate-700 dark:bg-[#131824] dark:text-white rounded-xl px-3 py-2 text-sm"
                >
                  <option value={0}>{t('dealScanner.matchAny')}</option>
                  <option value={50}>{t('dealScanner.matchModerate')}</option>
                  <option value={70}>{t('dealScanner.matchStrong')}</option>
                  <option value={85}>{t('dealScanner.matchExcellent')}</option>
                </select>
                <Button
                  data-testid="scanner-reset-filters"
                  onClick={() => setFilters({ minMatch: 0 })}
                  variant="ghost"
                  className="text-xs"
                >
                  {t('dealScanner.resetFilters')}
                </Button>
              </div>
            </div>

            {/* Top matches */}
            {sortBy === 'best_match' && (
              <TopMatches
                matches={results.top_matches}
                onAnalyze={handleAnalyze}
                onShare={handleShare}
              />
            )}

            {/* Full list */}
            {filteredResults.length === 0 ? (
              <div data-testid="scanner-no-results" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center bg-white/60 dark:bg-[#131824]/60">
                <h3 className="text-lg font-bold text-[#12355B] dark:text-white">
                  {t('dealScanner.noResultsTitle')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
                  {t('dealScanner.noResultsDesc')}
                </p>
                <ul className="text-xs text-slate-500 mt-3 space-y-1">
                  <li>• {t('dealScanner.tipIncreasePrice')}</li>
                  <li>• {t('dealScanner.tipLowerCashFlow')}</li>
                  <li>• {t('dealScanner.tipExpandArea')}</li>
                </ul>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredResults.map((r) => (
                  <DealCard
                    key={r.listing_id}
                    result={r}
                    onAnalyze={handleAnalyze}
                    onShare={handleShare}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
